import i18n, { InitOptions } from 'i18next';
import ChainedBackend from 'i18next-chained-backend';
import { DefaultNamespace, Fallback, initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

export const defaultNS = 'common';

export type Language = 'en' | 'es';
export const langs: Language[] = ['en', 'es'];

export const options: InitOptions = {
  fallbackLng: 'en',
  supportedLngs: langs,

  // Lazy load all namespaces on the client (needed ones are loaded in by SSR or with a supsense call)
  ns: [],
  defaultNS,

  debug: false,

  react: {
    useSuspense: false,
    transKeepBasicHtmlNodesFor: ['p', 'strong', 'br', 'em'],
  },

  interpolation: {
    escapeValue: false, // react already safe from xss
  },
};

/**
 * Load in our ENGLISH translation keys directly from the `.lang.ts` files so we can update them in the UI
 * immediately without a build step. This also makes sure we always have a base set of translations loaded.
 *
 * @returns the resource bundle keyed under `en`.
 */
async function loadNamespaces() {
  const rootLocale = (await import(/* webpackChunkName: 'english.lang' */ '.')).default;
  return { en: rootLocale };
}

/**
 * Inits and then passes a callback and middleware to the i18n instance
 *
 * @returns A promise
 */
export async function init(i18nOptions: InitOptions, backend: any, callback?: () => void) {
  const resources = await loadNamespaces();
  i18n.use(ChainedBackend).use(initReactI18next);
  if (!i18n.isInitialized) {
    return i18n.init(
      {
        ...i18nOptions,
        backend: {
          // @ts-ignore
          backends: [resourcesToBackend(resources), backend],
          backendOptions: [{}, i18nOptions.backend],
        },
      },
      () => {
        callback?.();
      }
    );
  }
  return Promise.resolve();
}

/**
 * Loads required namespaces into a route
 *
 * @returns A string or list of namespaces
 */
export function requireNamespaces<N extends Fallback<string> = DefaultNamespace>(
  ...namespace: N[]
) {
  return {
    requiredNamespaces: namespace,
  };
}

export default i18n;
